const safeDecodeURIComponent = (segment: string): string => {
	try {
		return decodeURIComponent(segment);
	} catch {
		return segment;
	}
};

/**
 * Serializes Record<string, string[]> to a URL-safe string.
 * Format: key1:val1,val2|key2:val3,val4
 * Returns null (param removed from URL) when the record is empty.
 */
export const serializeRecord = (record: Record<string, string[]>): string | null => {
	const entries = Object.entries(record).filter(([, values]) => values.length > 0);
	if (entries.length === 0) return null;
	return entries.map(([key, values]) => `${encodeURIComponent(key)}:${values.map(encodeURIComponent).join(',')}`).join('|');
};

export const deserializeRecord = (value: string): Record<string, string[]> => {
	if (!value) return {};
	const result: Record<string, string[]> = {};
	for (const entry of value.split('|')) {
		const colonIdx = entry.indexOf(':');
		if (colonIdx === -1) continue;
		const key = safeDecodeURIComponent(entry.slice(0, colonIdx));
		const values = entry
			.slice(colonIdx + 1)
			.split(',')
			.map(safeDecodeURIComponent)
			.filter(Boolean);
		if (values.length > 0) {
			result[key] = values;
		}
	}
	return result;
};

/**
 * Serializes string[] for a single query param (comma-separated, URI-encoded values).
 * Returns null when the list is empty so the param can be omitted from the URL.
 */
export const serializeStringList = (items: string[]): string | null => {
	if (items.length === 0) return null;
	return items.map(encodeURIComponent).join(',');
};

export const deserializeStringList = (value: string): string[] => {
	if (!value) return [];
	return value.split(',').map(safeDecodeURIComponent).filter(Boolean);
};
